import { CurrencyMismatchError, InvalidPositionError } from './errors';
import { fromDecimal, toDecimal } from './money';
import type { CurrencyCode } from './types';

/** สถานะการถือครองแบบย่อ — พอสำหรับการคำนวณถัวเฉลี่ย */
export interface Position {
  /** decimal string */
  quantity: string;
  /** decimal string — ต้นทุนเฉลี่ยต่อหน่วย */
  avgCost: string;
  currency: CurrencyCode;
}

export interface AddToPositionInput {
  /** decimal string — จำนวนที่ซื้อเพิ่ม ต้องมากกว่า 0 */
  quantity: string;
  /** decimal string — ราคาต่อหน่วยที่ซื้อ ต้องไม่ติดลบ */
  price: string;
  currency: CurrencyCode;
}

/**
 * ซื้อเพิ่ม — ถัวเฉลี่ยถ่วงน้ำหนัก (spec Section 5.3)
 *
 *   newQuantity = quantity + addQty
 *   newAvgCost  = (quantity × avgCost + addQty × addPrice) / newQuantity
 *
 * บังคับให้สกุลเงินตรงกัน แล้ว throw ถ้าไม่ตรง — ไม่แปลงอัตโนมัติเงียบ ๆ
 * เพราะการแปลงค่าเงินตอนถัวเฉลี่ยจะทำให้ต้นทุนเพี้ยนโดย user ไม่รู้ตัว
 */
export function addToPosition(current: Position, add: AddToPositionInput): Position {
  if (current.currency.toUpperCase() !== add.currency.toUpperCase()) {
    throw new CurrencyMismatchError(current.currency, add.currency);
  }

  const currentQty = toDecimal(current.quantity, 'position.quantity');
  const currentAvgCost = toDecimal(current.avgCost, 'position.avgCost');
  const addQty = toDecimal(add.quantity, 'add.quantity');
  const addPrice = toDecimal(add.price, 'add.price');

  if (addQty.lessThanOrEqualTo(0)) {
    throw new InvalidPositionError('จำนวนที่ซื้อเพิ่มต้องมากกว่า 0');
  }
  if (addPrice.isNegative()) {
    throw new InvalidPositionError('ราคาที่ซื้อต้องไม่ติดลบ');
  }
  if (currentQty.isNegative()) {
    throw new InvalidPositionError('จำนวนที่ถืออยู่ต้องไม่ติดลบ');
  }

  const newQuantity = currentQty.plus(addQty);
  const totalCost = currentQty.mul(currentAvgCost).plus(addQty.mul(addPrice));
  const newAvgCost = totalCost.div(newQuantity);

  return {
    quantity: fromDecimal(newQuantity),
    avgCost: fromDecimal(newAvgCost),
    currency: current.currency,
  };
}

/**
 * ขายบางส่วน (spec Section 5.4) — ต้นทุนเฉลี่ยไม่เปลี่ยน มีแต่จำนวนที่ลด
 *
 * คืน `null` เมื่อขายหมดพอดี แปลว่าผู้เรียกต้องลบ holding ทิ้ง
 * (MVP ไม่เก็บ realized PnL — spec Section 12 Phase 3)
 */
export function reducePosition(current: Position, sellQuantity: string): Position | null {
  const currentQty = toDecimal(current.quantity, 'position.quantity');
  const sellQty = toDecimal(sellQuantity, 'sellQuantity');

  if (sellQty.lessThanOrEqualTo(0)) {
    throw new InvalidPositionError('จำนวนที่ขายต้องมากกว่า 0');
  }
  if (sellQty.greaterThan(currentQty)) {
    throw new InvalidPositionError(
      `ขายเกินจำนวนที่ถืออยู่ (ถือ ${current.quantity} แต่จะขาย ${sellQuantity})`,
    );
  }

  const newQuantity = currentQty.minus(sellQty);
  if (newQuantity.isZero()) {
    return null;
  }

  return {
    quantity: fromDecimal(newQuantity),
    avgCost: current.avgCost,
    currency: current.currency,
  };
}
